import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { useInstitutionSettings } from "@/hooks/useInstitutionSettings";
import {
  LayoutDashboard,
  Users,
  FileText,
  Building2,
  UserCheck,
  DollarSign,
  BarChart3,
  FileBarChart,
  ShieldCheck,
  History,
  Settings,
  MessageSquare,
  HelpCircle,
  Brain,
  GraduationCap,
  Clock,
  Upload,
  Bell,
  Receipt,
  TrendingUp,
  RefreshCw,
} from "lucide-react";

const navigation = [
  { 
    name: "Overview", 
    href: "/dashboard", 
    icon: LayoutDashboard,
    roles: ["super_admin", "compliance_officer", "athletic_director", "coach"]
  },
  { 
    name: "Athletes", 
    href: "/dashboard/athletes", 
    icon: Users,
    roles: ["super_admin", "compliance_officer", "athletic_director", "coach"]
  },
  { 
    name: "Deals", 
    href: "/dashboard/deals", 
    icon: FileText,
    roles: ["super_admin", "compliance_officer", "athletic_director", "coach"]
  },
  { 
    name: "Renewals", 
    href: "/dashboard/renewals", 
    icon: RefreshCw,
    roles: ["super_admin", "compliance_officer", "athletic_director", "coach"]
  },
  { 
    name: "Sponsors", 
    href: "/dashboard/sponsors", 
    icon: Building2,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Associated Entities", 
    href: "/dashboard/entities", 
    icon: UserCheck,
    roles: ["super_admin", "compliance_officer"]
  },
  { 
    name: "CAPS Management", 
    href: "/dashboard/caps", 
    icon: DollarSign,
    roles: ["super_admin", "athletic_director"]
  },
  { 
    name: "Analytics", 
    href: "/dashboard/analytics", 
    icon: BarChart3,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Reports & Export", 
    href: "/dashboard/reports", 
    icon: FileBarChart,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "AI Showcase", 
    href: "/dashboard/ai-showcase", 
    icon: Brain,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Eligibility", 
    href: "/dashboard/eligibility", 
    icon: GraduationCap,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Financial Aid", 
    href: "/dashboard/financial-aid", 
    icon: DollarSign,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Time Tracking", 
    href: "/dashboard/time-tracking", 
    icon: Clock,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Recruitment", 
    href: "/dashboard/recruitment", 
    icon: Users,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Compliance", 
    href: "/dashboard/compliance", 
    icon: ShieldCheck,
    roles: ["super_admin", "compliance_officer", "athletic_director"]
  },
  { 
    name: "Audit Trail", 
    href: "/dashboard/audit", 
    icon: History,
    roles: ["super_admin", "compliance_officer"]
  },
  { 
    name: "NIL Go Export", 
    href: "/dashboard/nil-go-export", 
    icon: Upload,
    roles: ["super_admin", "compliance_officer"]
  },
  { 
    name: "Rule Changes", 
    href: "/dashboard/admin/rule-changes", 
    icon: Bell,
    roles: ["super_admin", "compliance_officer"]
  },
  { 
    name: "FMV Market Rates", 
    href: "/dashboard/fmv-market-rates", 
    icon: TrendingUp,
    roles: ["super_admin", "compliance_officer"]
  },
  { 
    name: "Help Center", 
    href: "/dashboard/help", 
    icon: HelpCircle,
    roles: ["super_admin", "compliance_officer", "athletic_director", "coach"]
  },
  { 
    name: "User Management", 
    href: "/dashboard/admin/users", 
    icon: UserCheck,
    roles: ["super_admin"]
  },
  { 
    name: "Settings", 
    href: "/dashboard/settings", 
    icon: Settings,
    roles: ["super_admin", "compliance_officer"]
  },
  { 
    name: "Messages", 
    href: "/dashboard/messages", 
    icon: MessageSquare,
    roles: ["super_admin", "compliance_officer", "athletic_director", "coach"]
  },
];

export const DashboardSidebar = () => {
  const { hasAnyRole } = useUserRole();
  const { settings } = useInstitutionSettings();

  // Filter navigation items based on user roles and feature flags
  const visibleNavItems = navigation.filter(item => {
    // Check role permissions first
    if (item.roles && !hasAnyRole(item.roles as any)) {
      return false;
    }
    
    // Check feature flags for optional modules
    // (All current items are enabled by default, future items can be gated here)
    
    return true;
  });

  return (
    <aside className="w-64 border-r bg-card">
      <div className="flex h-16 items-center border-b px-6">
        <h1 className="text-xl font-bold text-primary">AthleteNexus</h1>
      </div>
      <nav className="flex flex-col gap-1 p-4">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === "/dashboard"}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.name}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
};
